import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { WaitlistEntryPublic } from '@rx-manager/shared';
import { BookingApiError, waitlistApi } from '../../lib/api';
import {
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Info,
  Link as LinkIcon,
  LogOut,
  MapPin,
  PauseCircle,
  type LucideIcon,
} from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { Alert, Button, Card, HEADING, IconTile, Spinner, cn } from '../../components/ui';
import { Detail } from '../../components/AppointmentCard';
import { useShellBrand } from '../../components/Shell';
import { wantedText } from './WaitlistJoin';

/**
 * Track BCW (BCW-D11) — the patient's own place on the waitlist (`/w/<token>`): what it is for,
 * when they can come, until when, and "Leave the waitlist". After a pause for offers not taken,
 * "Keep me on the list" (BCW-D21). Nothing about the patient: whoever holds the link reads the
 * same page.
 */
const ACTIVE = new Set(['waiting', 'offered', 'paused']);

export function WaitlistEntryPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const [entry, setEntry] = useState<WaitlistEntryPublic | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const names = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', timeZone: 'UTC' }).format(
          new Date(Date.UTC(2024, 0, 1 + i, 12)),
        ),
      ),
    [locale],
  );

  useEffect(() => {
    let cancelled = false;
    void waitlistApi
      .entry(token)
      .then((result) => {
        if (cancelled) return;
        setEntry(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(error instanceof BookingApiError && error.isMissing ? 'missing' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // The band names the pharmacy once the link resolves.
  useShellBrand(entry ? { name: entry.pharmacy.name, phone: entry.pharmacy.phone } : null);

  const act = async (call: () => Promise<WaitlistEntryPublic>): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      setEntry(await call());
      setConfirming(false);
    } catch (error) {
      setFailure(error instanceof BookingApiError ? error.message : t('error.offline'));
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') return <Spinner label={t('time.loading')} />;
  if (state === 'missing') {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <IconTile icon={LinkIcon} tone="muted" size="lg" />
        <p className="max-w-sm text-base text-muted-foreground">{t('entry.missing')}</p>
      </div>
    );
  }
  if (state === 'error' || !entry) return <Alert>{t('error.unknown')}</Alert>;

  const keepable = entry.status === 'paused' && entry.endReason === 'lapsed';
  const tone = STATUS_TONE[entry.status] ?? 'muted';
  const StatusIcon = STATUS_ICON[tone];
  return (
    <div className="space-y-6">
      <h1 tabIndex={-1} className={HEADING}>
        {t('entry.heading')}
      </h1>
      {failure && <Alert>{failure}</Alert>}
      <p
        role="status"
        className={cn('flex items-start gap-3 rounded-xl border p-4 text-base font-medium text-heading', STATUS_BOX[tone])}
      >
        <StatusIcon className={cn('mt-0.5 h-5 w-5 shrink-0', STATUS_ICON_CLASS[tone])} aria-hidden="true" />
        {t(`entry.status.${entry.status}`)}
      </p>
      <dl className="divide-y divide-border rounded-xl border border-border px-4 sm:px-5">
        <Detail icon={ClipboardList} label={t('step.service')} value={entry.serviceTitle} />
        <Detail icon={MapPin} label={t('waitlist.where')} value={entry.location?.name ?? t('waitlist.anyBranch')} />
        <Detail icon={CalendarClock} label={t('waitlist.when')} value={wantedText(entry.days, entry.parts, names, t)} />
        <Detail icon={CalendarCheck} label={t('waitlist.endsOn')} value={entry.endsOn} />
      </dl>
      {entry.status === 'paused' && !keepable && (
        <p className="text-base text-foreground">
          {t('entry.pausedCall')}{' '}
          {entry.pharmacy.phone && (
            <a href={`tel:${entry.pharmacy.phone}`} className="font-semibold text-primary-strong underline underline-offset-4">
              {entry.pharmacy.phone}
            </a>
          )}
        </p>
      )}

      {keepable && (
        <Button onClick={() => void act(() => waitlistApi.keep(token))} loading={busy}>
          {t('entry.keep')}
        </Button>
      )}

      {ACTIVE.has(entry.status) &&
        (confirming ? (
          <Card tone="muted" className="space-y-4">
            <p className="text-base font-medium text-heading">{t('entry.leaveQuestion')}</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setConfirming(false)}>
                {t('entry.stay')}
              </Button>
              <Button variant="danger" onClick={() => void act(() => waitlistApi.leave(token))} loading={busy}>
                {t('entry.leaveConfirm')}
              </Button>
            </div>
          </Card>
        ) : (
          <Button variant="outline" onClick={() => setConfirming(true)}>
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('entry.leave')}
          </Button>
        ))}
    </div>
  );
}

type Tone = 'success' | 'warning' | 'muted';

/** How each status reads at a glance: an icon and a word, never colour alone. */
const STATUS_TONE: Partial<Record<WaitlistEntryPublic['status'], Tone>> = {
  waiting: 'success',
  offered: 'success',
  booked: 'success',
  paused: 'warning',
};
const STATUS_ICON: Record<Tone, LucideIcon> = { success: CheckCircle2, warning: PauseCircle, muted: Info };
const STATUS_ICON_CLASS: Record<Tone, string> = {
  success: 'text-success',
  warning: 'text-warning-foreground',
  muted: 'text-muted-foreground',
};
const STATUS_BOX: Record<Tone, string> = {
  success: 'border-success/30 bg-success/5',
  warning: 'border-warning/50 bg-warning/15',
  muted: 'border-border bg-muted/60',
};
